import { Alert, Box, Button } from "@mui/material";

import {
  Control,
  UseFormResetField,
  UseFormSetValue,
  useFormState,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

import { can } from "authorization/authorization.util";
import { DevicePermissionEnum } from "authorization/types";
import { SelectInput, TextInput } from "components/forms";
import { SectionCard } from "components/SectionCard/SectionCard";
import { useAuthenticatedUser } from "hooks/useAuthenticatedUser";
import { useGetZones } from "hooks/useGetZones";

import { DeviceFormSchema } from "../forms/deviceFormSchema";

type DeviceInformationProps = {
  control: Control<DeviceFormSchema>;
  setValue: UseFormSetValue<DeviceFormSchema>;
  resetField: UseFormResetField<DeviceFormSchema>;
};

export function DeviceInformation({
  control,
  setValue,
  resetField,
}: DeviceInformationProps) {
  const { t } = useTranslation();
  const { permissions } = useAuthenticatedUser();

  const { touchedFields, defaultValues } = useFormState({
    control,
    name: "zone_id",
  });

  const { zones } = useGetZones();

  const disabled = !can(permissions, DevicePermissionEnum["device.update"]);

  // Show warning if the user has changed the zone and there is currently an assigned subject
  const showZoneWarning =
    !!touchedFields?.zone_id && !!defaultValues?.subject_id;

  return (
    <SectionCard title={t("Device Information")} gridArea={"info"}>
      <Box
        display={"flex"}
        width={"100%"}
        flexDirection={"column"}
        sx={{ "> div": { marginBottom: 2 } }}
      >
        <SelectInput
          control={control}
          fieldName={"zone_id"}
          label={t("Zone")}
          disabled={disabled}
          options={zones.map((zone) => ({
            ...zone,
            label: zone.name,
            disabled: !zone.hasAccess,
            padding: (zone.level - 1) * 2,
          }))}
          placeholder={t("Select a zone")}
          onChange={(event) => {
            // Ensure the field is immediately marked as touched
            setValue("zone_id", event.target.value, { shouldTouch: true });
            // Set subject_id to null, as the current subject does not exist in the new zone
            setValue("subject_id", null);
          }}
        />
        {showZoneWarning && (
          <Alert
            severity={"warning"}
            action={
              <Button
                color={"warning"}
                onClick={() => {
                  resetField("zone_id");
                  resetField("subject_id");
                }}
              >
                {t("reset")}
              </Button>
            }
          >
            {t("device_update_change_zone_popup_body")}
          </Alert>
        )}
        <TextInput
          fieldName={"name"}
          control={control}
          label={t("Name")}
          disabled={disabled}
          variant={"outlined"}
          required
        />
        <TextInput
          control={control}
          fieldName={"serial_number"}
          label={t("Serial Number")}
          disabled
          variant={"outlined"}
        />
      </Box>
    </SectionCard>
  );
}
